import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { FlagIcon, UserGroupIcon, TrophyIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import { safeParseJSON } from '../lib/contentUtils';
import type { ContentItem } from '../types/content';

const featureIcons = {
  0: FlagIcon,
  1: UserGroupIcon,
  2: TrophyIcon
};

function AboutSection() {
  const [aboutContent, setAboutContent] = useState<{
    title: string;
    description: string;
    video: {
      type?: 'youtube' | 'video';
      url: string;
      fallbackImage: string;
      autoplay?: boolean;
      controls?: boolean;
      loop?: boolean;
      muted?: boolean;
      modestBranding?: boolean;
      showRelated?: boolean;
    };
    features: Array<{
      title: string;
      description: string;
    }>;
    stats: {
      players: string;
      prizeMoney: string;
      countries: string;
      spectators: string;
    };
  }>({
    title: 'About The Tournament',
    description: 'The Ibom Open is one of Nigeria\'s most prestigious golf tournaments, bringing together professional and amateur golfers from across Africa for an unforgettable championship experience.',
    video: {
      type: 'video',
      url: '',
      fallbackImage: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200',
      autoplay: false,
      controls: true,
      loop: true,
      muted: true,
      modestBranding: true,
      showRelated: false
    },
    features: [
      {
        title: "Championship Course",
        description: "Experience golf at its finest on our world-class course designed by Thomson Perrett & Lobb"
      },
      {
        title: "Elite Competition",
        description: "144 top players from across Africa competing for glory and prestigious titles"
      },
      {
        title: "Prize Pool",
        description: "₦50 Million in prizes distributed across professional and amateur divisions"
      }
    ],
    stats: {
      players: "300+",
      prizeMoney: "₦150M+",
      countries: "15+",
      spectators: "50,000+"
    }
  });

  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    fetchAboutContent();
  }, []);

  const fetchAboutContent = async () => {
    try {
      const { data: sections, error } = await supabase
        .from('content_sections')
        .select(`
          *,
          content_items (*)
        `)
        .eq('page', 'home')
        .eq('title', 'About Section')
        .eq('content_items.status', 'Published');

      if (error) throw error;

      if (sections && sections.length > 0) {
        const section = sections[0];
        if (section?.content_items && section.content_items.length > 0) {
          const items = section.content_items as ContentItem[];
          
          const titleItem = items.find(item => item.title === 'Section Title');
          const descriptionItem = items.find(item => item.title === 'Description');
          const videoItem = items.find(item => item.title === 'Video Content');
          const featuresItem = items.find(item => item.title === 'Features');
          const statsItem = items.find(item => item.title === 'Statistics');

          const parsedTitle = titleItem?.content ? safeParseJSON(titleItem.content) : aboutContent.title;
          const parsedDescription = descriptionItem?.content ? safeParseJSON(descriptionItem.content) : aboutContent.description;
          const parsedVideo = videoItem?.content ? 
            (typeof videoItem.content === 'string' ? safeParseJSON(videoItem.content) : videoItem.content) : 
            aboutContent.video;
          const parsedFeatures = featuresItem?.content ? 
            (typeof featuresItem.content === 'string' ? safeParseJSON(featuresItem.content) : featuresItem.content) : 
            aboutContent.features;
          const parsedStats = statsItem?.content ? 
            (typeof statsItem.content === 'string' ? safeParseJSON(statsItem.content) : statsItem.content) : 
            aboutContent.stats;

          setAboutContent({
            title: parsedTitle as string,
            description: parsedDescription as string,
            video: parsedVideo as typeof aboutContent.video,
            features: parsedFeatures as typeof aboutContent.features,
            stats: parsedStats as typeof aboutContent.stats
          });
        }
      }
    } catch (error) {
      console.error('Error fetching about content:', error);
      setVideoError(true);
    }
  };

  const renderVideo = () => {
    if (videoError || !aboutContent.video || !aboutContent.video.url) {
      return (
        <div className="rounded-3xl w-full aspect-[4/3] relative z-10 bg-gray-200 flex items-center justify-center overflow-hidden">
          <img
            src={aboutContent.video?.fallbackImage || "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200"}
            alt="Ibom Golf & Country Club tournament course"
            className="w-full h-full object-cover rounded-3xl"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = "https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200";
            }}
          />
        </div>
      );
    }

    if (aboutContent.video.type === 'youtube') {
      // Create a clean YouTube embed URL with all necessary parameters
      const youtubeEmbedUrl = new URL(`https://www.youtube.com/embed/${aboutContent.video.url}`);
      
      // Add required parameters
      youtubeEmbedUrl.searchParams.append('autoplay', aboutContent.video.autoplay ? '1' : '0');
      youtubeEmbedUrl.searchParams.append('controls', aboutContent.video.controls ? '1' : '0');
      youtubeEmbedUrl.searchParams.append('modestbranding', aboutContent.video.modestBranding ? '1' : '0');
      youtubeEmbedUrl.searchParams.append('rel', aboutContent.video.showRelated ? '1' : '0');
      youtubeEmbedUrl.searchParams.append('mute', '1');
      youtubeEmbedUrl.searchParams.append('origin', window.location.origin);
      
      return (
        <iframe
          key={`youtube-embed-${aboutContent.video.url}`}
          src={youtubeEmbedUrl.toString()}
          title="About Video"
          className="rounded-3xl w-full aspect-[4/3] relative z-10"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          referrerPolicy="strict-origin"
          onError={() => setVideoError(true)}
        />
      );
    }

    return (
      <video
        className="rounded-3xl w-full object-cover aspect-[4/3] relative z-10"
        autoPlay={aboutContent.video.autoplay}
        loop={aboutContent.video.loop}
        muted={aboutContent.video.muted}
        controls={aboutContent.video.controls}
        playsInline
        poster={aboutContent.video.fallbackImage}
        onError={() => setVideoError(true)}
      >
        <source src={aboutContent.video.url} type="video/mp4" />
      </video>
    );
  };

  return (
    <section className="py-32 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto"
        >
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Column - Video */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-golf-orange/10 rounded-3xl blur-xl"></div>
                {renderVideo()}
              </div>
            </motion.div>

            {/* Right Column - Content */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-golf-gray mb-6">
                  {aboutContent.title}
                </h2>
                <p className="text-lg md:text-xl text-gray-600 mb-8">
                  {aboutContent.description}
                </p>
              </motion.div>

              <div className="space-y-8">
                {aboutContent.features.map((feature, index) => {
                  const Icon = featureIcons[index as keyof typeof featureIcons] || FlagIcon;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start space-x-4"
                    >
                      <div className="flex-shrink-0">
                        <Icon className="w-8 h-8 text-golf-orange" />
                      </div>
                      <div>
                        <h3 className="text-lg md:text-xl font-bold text-golf-gray mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600">
                          {feature.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="pt-8"
              >
                <Link
                  to="/about"
                  className="modern-button inline-block px-8 py-4 text-lg text-white font-medium"
                >
                  Learn More
                </Link>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default AboutSection;